import React, { useState, useRef, useEffect } from "react";
import { ChevronDown, Search, Check } from "lucide-react";
import { cn } from "@/lib/utils";
import { useLanguage } from "@/contexts/LanguageContext";
import { normalizeText } from "@/utils/textFormatter";

export interface SearchableSelectOption {
  label: string;
  value: string;
}

export interface SearchableSelectProps {
  placeholder?: string;
  options: SearchableSelectOption[];
  value: string;
  onChange: (value: string) => void;
  onBlur?: (value: string) => void;
  className?: string;
  disabled?: boolean;
  loading?: boolean;
}

export const SearchableSelect: React.FC<SearchableSelectProps> = ({
  placeholder = "Select...",
  options,
  value,
  onChange,
  onBlur,
  className,
  disabled = false,
  loading = false,
}) => {
  const { t } = useLanguage();
  const [searchQuery, setSearchQuery] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Filter options based on search query
  const filteredOptions = options.filter((option) => {
    const normalizedOptionLabel = normalizeText(option.label);
    const normalizedSearchQuery = normalizeText(searchQuery);
    return normalizedOptionLabel.includes(normalizedSearchQuery);
  }).sort((a, b) => a.label.localeCompare(b.label));

  // Get selected option label
  const selectedOption = options.find((option) => option.value === value);
  const displayText = selectedOption ? selectedOption.label : placeholder;

  // Handle clicks outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchQuery("");
        setHighlightedIndex(-1);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Focus search input when opened
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen]);

  const handleSelect = (optionValue: string) => {
    onChange(optionValue);
    setIsOpen(false);
    setSearchQuery("");
    setHighlightedIndex(-1);
    if (onBlur) {
      onBlur(optionValue);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen) {
      if (e.key === "Enter" || e.key === " " || e.key === "ArrowDown") {
        e.preventDefault();
        setIsOpen(true);
      }
      return;
    }

    switch (e.key) {
      case "Escape":
        setIsOpen(false);
        setSearchQuery("");
        setHighlightedIndex(-1);
        break;
      case "ArrowDown":
        e.preventDefault();
        setHighlightedIndex((prev) => (prev < filteredOptions.length - 1 ? prev + 1 : 0));
        break;
      case "ArrowUp":
        e.preventDefault();
        setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : filteredOptions.length - 1));
        break;
      case "Enter":
        e.preventDefault();
        if (highlightedIndex >= 0 && filteredOptions[highlightedIndex]) {
          handleSelect(filteredOptions[highlightedIndex].value);
        }
        break;
    }
  };

  return (
    <div className="relative" ref={containerRef}>
      {/* Trigger Button */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        onKeyDown={handleKeyDown}
        disabled={disabled}
        className={cn(
          "w-full h-12 px-3 py-2 text-left bg-white border border-gray-200 rounded-md",
          "flex items-center justify-between",
          "hover:bg-gray-50 hover:border-gray-300",
          "focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500",
          disabled && "bg-gray-100 cursor-not-allowed opacity-70",
          isOpen && "border-teal-500 ring-2 ring-teal-500/20",
          className
        )}
      >
        <span
          className={cn("truncate", !selectedOption && "text-gray-500")}
          title={selectedOption ? selectedOption.label : undefined}
        >
          {loading ? t("common.loading") : displayText}
        </span>
        <ChevronDown className={cn("h-4 w-4 transition-transform", isOpen && "rotate-180")} />
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div
          ref={dropdownRef}
          className="absolute top-full left-0 right-0 mt-2 bg-white border border-gray-200 rounded-md shadow-lg z-[9999]"
        >
          {/* Search Input */}
          <div className="p-2 border-b border-gray-200">
            <div className="relative">
              <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                ref={searchInputRef}
                type="text"
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setHighlightedIndex(-1);
                }}
                onKeyDown={handleKeyDown}
                placeholder={t("common.searchPlaceholder")}
                className="w-full pl-8 pr-2 py-1.5 text-sm border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-teal-500 focus:border-teal-500"
              />
            </div>
          </div>

          {/* Options List */}
          <div
            className="max-h-60 overflow-y-auto py-1"
            style={{
              scrollbarWidth: "thin",
              scrollbarColor: "#d1d5db #f9fafb",
            }}
          >
            {loading ? (
              <div className="px-3 py-2 text-center text-gray-500">
                <div className="inline-block h-4 w-4 border-2 border-gray-300 border-t-teal-500 rounded-full animate-spin mr-2"></div>
                {t("common.loading")}
              </div>
            ) : filteredOptions.length > 0 ? (
              filteredOptions.map((option, index) => (
                <button
                  key={option.value}
                  onClick={() => handleSelect(option.value)}
                  className={cn(
                    "w-full px-3 py-2 text-left text-sm hover:bg-gray-100",
                    "flex items-start justify-between gap-2",
                    "focus:outline-none focus:bg-gray-100",
                    value === option.value && "bg-teal-50 text-teal-900",
                    highlightedIndex === index && "bg-gray-100"
                  )}
                >
                  <span className="whitespace-normal break-words">{option.label}</span>
                  {value === option.value && <Check className="h-4 w-4 shrink-0 text-teal-600" />}
                </button>
              ))
            ) : (
              <div className="px-3 py-2 text-center text-gray-500 text-sm">
                {searchQuery ? t("common.noDataAvailable") : "No options available"}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
